import React, { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";

import { useAlert } from "../../contexts/AlertContext";
import { ATPUser } from "../../services/api";

const AddUserMenu: React.FC = () => {
  const [newUser, setNewUser] = useState<ATPUser>(new ATPUser({ username: "", role: "guest" }));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { alert } = useAlert();

  const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewUser(new ATPUser({ username: event.target.value, role: newUser.role }));
  };

  const handleRoleChange = (event: SelectChangeEvent) => {
    setNewUser(new ATPUser({ username: newUser.username, role: event.target.value }));
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleAddUser = () => {
    newUser
      .create()
      .then(() => {
        alert.success("User created successfully.");
        new Promise((resolve) => setTimeout(resolve, 1000)).then(() => {
          document.location.reload();
        });
      })
      .catch(() => {
        alert.error("Error creating user.");
      });
  };

  const hasUsernameError = newUser.hasError("username");
  const hasRoleError = newUser.hasError("role");

  return (
    <>
      <Grid item sx={{ textAlign: "right", pt: "5px" }} md={7} sm={12}>
        <Stack direction="row" spacing={2} alignItems="right" justifyContent="flex-end">
          <Button
            size="small"
            variant="contained"
            onClick={handleMenuOpen}
            sx={{ float: "right", marginTop: "10px" }}
          >
            ADD USER
          </Button>
        </Stack>
      </Grid>
      <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
        <Box
          component={"form"}
          name={"add"}
          sx={{ display: "flex", flexDirection: "row", alignItems: "center", p: 2, width: "500px" }}
        >
          <IconButton
            color="default"
            size="small"
            onClick={handleMenuClose}
            sx={{ marginRight: "auto" }}
            aria-label={"Close"}
          >
            <CloseIcon />
          </IconButton>
          <TextField
            required
            type="string"
            label="SSO Number"
            variant="standard"
            autoComplete={"off"}
            error={!!hasUsernameError}
            helperText={hasUsernameError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {hasUsernameError ? (
                    <ErrorIcon sx={{ color: "red" }} data-testid={"error-icon-SSO_Number"} />
                  ) : (
                    <CheckCircleIcon sx={{ color: "green" }} data-testid={"valid-icon-SSO_Number"} />
                  )}
                </InputAdornment>
              ),
            }}
            value={newUser.username}
            onChange={handleUsernameChange}
            size="medium"
            placeholder="123456789"
            sx={{ mx: 1, flex: 1 }}
          />
          <FormControl sx={{ m: 1, minWidth: 120 }} error={!!hasRoleError}>
            <InputLabel id="role-label">Role</InputLabel>
            <Select
              labelId="role-label"
              value={newUser.role}
              label="Role"
              onChange={handleRoleChange}
              displayEmpty
              inputProps={{ "data-testid": "content-selection" }}
            >
              <MenuItem value="guest">Guest</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="operator">Operator</MenuItem>
            </Select>
            <FormHelperText data-testid="error-text-mapped_sets" error={!!hasRoleError}>
              {hasRoleError ?? null}
            </FormHelperText>
          </FormControl>
          <IconButton
            color="default"
            size="small"
            onClick={handleAddUser}
            sx={{ marginLeft: "auto" }}
            aria-label={"Add"}
            disabled={!!hasUsernameError || !!hasRoleError}
          >
            <PersonAddIcon />
          </IconButton>
        </Box>
      </Menu>
    </>
  );
};

export default AddUserMenu;
